import { useState, type FormEvent } from 'react';
import type { BitrixCredentials } from '@tailorcast/shared-types';
import { useSaveCredentials } from '../hooks/useCredentials';

interface CredentialsFormProps {
  initial?: BitrixCredentials;
}

const emptyCredentials: BitrixCredentials = {
  webhookUrl: '',
  catalogIblockId: '',
  catalogGroupId: '',
};

export function CredentialsForm({ initial }: CredentialsFormProps) {
  const [form, setForm] = useState<BitrixCredentials>(initial ?? emptyCredentials);
  const save = useSaveCredentials();

  const update = (key: keyof BitrixCredentials, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    save.reset();
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync(form);
    } catch {
      // error is available via save.error
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div>
        <label htmlFor="webhookUrl" className="block text-sm font-medium text-gray-700 mb-1">
          Webhook URL
        </label>
        <input
          id="webhookUrl"
          type="text"
          value={form.webhookUrl}
          onChange={(e) => update('webhookUrl', e.target.value)}
          required
          className="input"
          placeholder="https://mystore.bitrix24.ru/rest/1/abc123def456/"
        />
      </div>

      <div>
        <label htmlFor="catalogIblockId" className="block text-sm font-medium text-gray-700 mb-1">
          Catalog Infoblock ID
        </label>
        <input
          id="catalogIblockId"
          type="text"
          value={form.catalogIblockId}
          onChange={(e) => update('catalogIblockId', e.target.value)}
          required
          className="input"
          placeholder="14"
        />
      </div>

      <div>
        <label htmlFor="catalogGroupId" className="block text-sm font-medium text-gray-700 mb-1">
          Price Type ID
        </label>
        <input
          id="catalogGroupId"
          type="text"
          value={form.catalogGroupId}
          onChange={(e) => update('catalogGroupId', e.target.value)}
          className="input"
          placeholder="2"
        />
      </div>

      {save.isSuccess && save.data && (
        <div
          className={`text-sm rounded-lg p-3 ${
            save.data.isValid
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
          }`}
        >
          {save.data.message}
          {save.data.isValid && save.data.catalogName && (
            <span className="block mt-1">
              Catalog: {save.data.catalogName} ({save.data.productCount} products)
            </span>
          )}
        </div>
      )}

      {save.error && (
        <div className="text-sm rounded-lg p-3 bg-red-50 border border-red-200 text-red-700">
          {save.error instanceof Error ? save.error.message : 'Save failed'}
        </div>
      )}

      <div className="flex gap-3">
        <button type="submit" disabled={save.isPending} className="btn btn-primary">
          {save.isPending ? 'Saving...' : 'Save & Validate'}
        </button>
      </div>
    </form>
  );
}
